import type { PageTemplatePreviewModalProps } from "@editor/components/AppModalTypes";
import type { File } from "schemas/generated/file";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import PreviewThumbnail from "@/features/funnel/components/PreviewThumbnail";
import { useFetchPageFilesForWebContainer } from "@/features/web-container/useFetchPageFilesForWebContainer";
import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const PageTemplatePreviewModal: React.FC<
  PageTemplatePreviewModalProps
> = ({ template, chatId, onSuccess }) => {
  const { closeModal } = useModal();
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();

  const fetchPageFiles = useFetchPageFilesForWebContainer();

  const handleNewPageSuccess = async ({
    createdFile,
  }: {
    createdFile: File;
  }) => {
    void trpcUtils.nextJSProject.listPages.invalidate({ projectId });
    // NOTE (Gabe 2025-08-08): We await the fetchPageFiles call to ensure that
    // the page is available for rendering before navigating.
    await fetchPageFiles([{ id: createdFile.id }]);
    // NOTE (Bobby, 2025-08-09): Waiting here for a little bit to ensure that page is available. Even though we await the fetchPageFiles call it seems like
    // the page still needs some time to be added to the file tree. Before it would work 50% of the time but with this timeout it works more consistently.
    // Real fix is to figure out what's actually causing the delay but adding this for now.
    await new Promise((resolve) => setTimeout(resolve, 1500));
    onSuccess?.(createdFile.id);
    closeModal({ type: "pageTemplatePreviewModal" });
    navigate(
      `/shops/${projectId}/chat?pageId=${createdFile.id}${
        chatId ? `&chatId=${chatId}` : ""
      }`,
    );
  };

  const { mutate: createPageFromTemplate, isPending: isCreatingPage } =
    trpc.nextJSProject.createPageFromTemplate.useMutation({
      onSuccess: handleNewPageSuccess,
    });

  const handleUseTemplate = () => {
    if (projectId) {
      createPageFromTemplate({
        projectId,
        templateId: template.id,
      });
    }
  };

  return (
    <Modal
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "pageTemplatePreviewModal" });
        }
      }}
      isClosable={false}
      size="lg"
      UNSAFE_className="p-0"
    >
      <div className="flex flex-col w-full justify-center gap-2">
        <div className="flex flex-row w-full justify-between items-start border-b-0.5 border-border pt-0 pb-6 px-6">
          <div className="flex flex-col gap-1">
            <h2 className="header-h2 text-default">{template.name}</h2>
            <p className="text-muted text-base">{template.category}</p>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <Button
              variant="secondary"
              size="base"
              onClick={() => {
                closeModal({ type: "pageTemplatePreviewModal" });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              UNSAFE_className="flex flex-row items-center justify-center"
              size="base"
              onClick={() => {
                void handleUseTemplate();
              }}
              isLoading={isCreatingPage}
            >
              Use Template
              <div className="flex pl-1 items-center justify-center">
                <ArrowRight size={16} />
              </div>
            </Button>
          </div>
        </div>
        <div className="p-6 flex justify-center items-center">
          <PreviewThumbnail
            pageRoute={`/template-preview/${template.id}`}
            className="w-[500px] h-[600px] border-0.5 border-border rounded-lg"
          />
        </div>
      </div>
    </Modal>
  );
};
